import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, MessageCircle, AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";

const STATUS_STYLE = {
  queued: "bg-blue-500/15 text-blue-300",
  provider_not_configured: "bg-amber-500/15 text-amber-300",
  skipped: "bg-slate-500/15 text-slate-300",
  sent: "bg-emerald-500/15 text-emerald-300",
  delivered: "bg-emerald-500/15 text-emerald-300",
  failed: "bg-red-500/15 text-red-300",
};

export default function AdminWhatsApp() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [token, setToken] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);

  const loadAll = async () => {
    try {
      const [s, t, l] = await Promise.all([
        api.get("/admin/whatsapp/settings"),
        api.get("/admin/whatsapp/templates"),
        api.get("/admin/whatsapp/logs"),
      ]);
      setSettings(s.data); setTemplates(t.data); setLogs(l.data);
    } catch { toast.error("Failed to load WhatsApp config"); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/whatsapp/settings", { ...settings, ...patch });
      setSettings(data); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCreds = async () => {
    if (!token.trim()) return;
    setSavingCreds(true);
    try { await api.post("/admin/whatsapp/credentials", { api_token: token.trim() }); toast.success("Credentials stored"); setToken(""); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCreds(false); }
  };
  const saveTemplate = async (ev, patch) => {
    try { await api.put(`/admin/whatsapp/templates/${ev}`, patch); toast.success("Template saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const configured = settings.provider_configured;

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle className="w-6 h-6 text-teal-400" />
        <h1 className="text-2xl font-bold text-white">WhatsApp Notifications</h1>
      </div>

      {!configured && (
        <div className="mb-5 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200" data-testid="wa-not-configured">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Provider not configured</div>
            <p className="text-sm text-amber-200/80 mt-1">The WhatsApp architecture is active and production-ready, but no provider is connected yet. Events are logged but <b>no messages are sent</b>. Set a provider and credentials to activate delivery.</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="settings">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="settings" data-testid="wa-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="wa-tab-logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4">
          <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-5 max-w-2xl">
            <div className="flex items-center justify-between">
              <div><div className="font-medium text-white">Channel enabled</div><p className="text-xs text-slate-400">Master switch for all WhatsApp notifications</p></div>
              <Switch checked={!!settings.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="wa-enabled" />
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-4">
              <div><div className="font-medium text-white">Transactional messages</div><p className="text-xs text-slate-400">Order, payment, invoice, delivery, refund</p></div>
              <Switch checked={!!settings.transactional_enabled} onCheckedChange={(v) => saveSettings({ transactional_enabled: v })} data-testid="wa-transactional" />
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-4">
              <div><div className="font-medium text-white">Marketing messages</div><p className="text-xs text-slate-400">Launch / upcoming notifications (requires customer consent)</p></div>
              <Switch checked={!!settings.marketing_enabled} onCheckedChange={(v) => saveSettings({ marketing_enabled: v })} data-testid="wa-marketing" />
            </div>
            <div className="border-t border-white/5 pt-4 grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Provider</Label>
                <select className="mt-1 w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-sm text-slate-200" value={settings.provider || ""} onChange={(e) => saveSettings({ provider: e.target.value })} data-testid="wa-provider">
                  <option value="">— Not configured —</option>
                  <option value="meta_cloud">Meta WhatsApp Cloud API</option>
                  <option value="twilio">Twilio WhatsApp</option>
                  <option value="gupshup">Gupshup</option>
                  <option value="interakt">Interakt</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div><Label className="text-xs text-slate-400">Sender number</Label>
                <Input className="mt-1 bg-slate-800 border-white/10 text-slate-200" placeholder="+91…" value={settings.sender_number || ""} onChange={(e) => setSettings({ ...settings, sender_number: e.target.value })} onBlur={(e) => saveSettings({ sender_number: e.target.value })} data-testid="wa-sender" />
              </div>
            </div>
            <div className="border-t border-white/5 pt-4">
              <Label className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-amber-400" /> API credentials (write-only, Super Admin)</Label>
              {isSuper ? (
                <div className="flex gap-2 mt-1">
                  <Input type="password" className="bg-slate-800 border-white/10 text-slate-200" placeholder={settings.api_token_configured ? "•••••••• (configured)" : "Enter provider API token"} value={token} onChange={(e) => setToken(e.target.value)} data-testid="wa-token" />
                  <Button onClick={saveCreds} disabled={savingCreds || !token.trim()} className="bg-teal hover:bg-teal-dark text-white" data-testid="wa-save-creds">{savingCreds ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
                </div>
              ) : <p className="text-xs text-slate-500 mt-1">Only a Super Admin can manage credentials.</p>}
              <p className="text-[11px] text-slate-500 mt-2">Secrets are stored securely and never displayed. {settings.api_token_configured && <span className="text-emerald-400">Token configured.</span>}</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="space-y-3 max-w-3xl">
            {templates.map((t) => (
              <div key={t.event} className="bg-slate-900 border border-white/10 rounded-xl p-4" data-testid={`wa-tmpl-${t.event}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">{t.label}</span>
                    <Badge className={t.kind === "marketing" ? "bg-fuchsia-500/15 text-fuchsia-300" : "bg-teal-500/15 text-teal-300"}>{t.kind}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">Active <Switch checked={t.active !== false} onCheckedChange={(v) => { setTemplates(templates.map((x) => x.event === t.event ? { ...x, active: v } : x)); saveTemplate(t.event, { active: v }); }} data-testid={`wa-tmpl-active-${t.event}`} /></div>
                </div>
                <Textarea rows={2} className="bg-slate-800 border-white/10 text-slate-200 text-sm" value={t.body || ""} onChange={(e) => setTemplates(templates.map((x) => x.event === t.event ? { ...x, body: e.target.value } : x))} data-testid={`wa-tmpl-body-${t.event}`} />
                <div className="mt-2 flex justify-end"><Button size="sm" variant="outline" onClick={() => saveTemplate(t.event, { body: t.body })} data-testid={`wa-tmpl-save-${t.event}`}>Save</Button></div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden" data-testid="wa-logs">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-white/10">
                <th className="p-3">Event</th><th className="p-3">To</th><th className="p-3">Kind</th><th className="p-3">Status</th><th className="p-3">When</th>
              </tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-white/5" data-testid={`wa-log-${l.id}`}>
                    <td className="p-3 text-slate-200">{l.event_label || l.event}</td>
                    <td className="p-3 text-slate-400">{l.to || "—"}</td>
                    <td className="p-3 text-slate-400">{l.kind}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLE[l.status] || "bg-slate-500/15 text-slate-300"}`}>{l.status}</span>{l.reason && <div className="text-[11px] text-slate-500 mt-0.5">{l.reason}</div>}</td>
                    <td className="p-3 text-slate-500 text-xs">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {!logs.length && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No WhatsApp events logged yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
